import utc from "dayjs/plugin/utc";
import dayjs, { Dayjs, OpUnitType } from "dayjs";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ZoomInOutlined,
} from "@ant-design/icons";
import {
  Bar,
  BarChart,
  Line,
  LineChart,
  ReferenceArea,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";
import { Button, theme } from "antd";
import { PropsWithChildren, useEffect, useMemo, useRef, useState } from "react";
import { useHover } from "usehooks-ts";
import { valueToLabel, valueToNumber } from "pages/[orgId]/explore/lib/common";
import { Flex } from "src/components/Flex";
import { useSubscribeEffect } from "src/hooks/useSubscribeEffect";
import { nextRange, previousDuration, previousRange } from "src/utils/date";
import { useDurationOrDatesToRange } from "src/hooks/useDurationOrDatesToRange";
import { DynamicCanvas } from "src/components/DynamicCanvas";
import { useUniqueId } from "src/hooks/useUniqueId";
import { singleColor } from "src/utils/color";
import { pluralize } from "src/utils/strings";
import { mono } from "src/fonts";
import { SearchActions } from "../types";
import { SearchContext } from "../useSearchContext";
import { usePopupContext } from "src/hooks/usePopup";
import { EmptyResultExplanation } from "pages/[orgId]/explore/lib/EmptyResultExplanation";

dayjs.extend(utc);

export function TimeSeries(props: {
  displayType?: "line" | "bars";
  hideLabels?: boolean;
  context: SearchContext;
  resizeTriggerKey?: string;
  height?: number;
}) {
  const { token } = theme.useToken();
  const { params, timeSeries } = props.context;

  const data = useTimeSeriesData({
    data: timeSeries.data?.data || [],
    timeUnit: ["days", "hours", "minutes"].includes(timeSeries.timeUnit)
      ? (timeSeries.timeUnit as "days" | "hours" | "minutes")
      : "days",
    start: params.state.startDate,
    end: params.state.endDate,
    duration: params.state.duration,
  });
  useSubscribeEffect(timeSeries);

  const id = useUniqueId();
  const displayType = props.displayType === "line" ? "line" : "bars";
  const Chart = displayType === "bars" ? BarChart : LineChart;

  const [start, setStart] = useState<[string, Dayjs] | undefined>(undefined);
  const [end, setEnd] = useState<[string, Dayjs] | undefined>(undefined);
  const [startX, startDayjs] = start || [];
  const [endX, endDayjs] = end || [];

  let refX1 = startX,
    refX2 = endX;
  if (startDayjs && endDayjs && endDayjs.isBefore(startDayjs)) {
    refX1 = endX;
    refX2 = startX;
  }

  const [allowTooltip, setAllowTooltip] = useState(true);
  const { setPopup, isOpen } = usePopupContext();
  useEffect(() => {
    if (!isOpen) {
      setStart(undefined);
      setEnd(undefined);
    }
  }, [isOpen]);

  const [isHovering, setIsHovering] = useState(false);

  if (!timeSeries.isLoading && (timeSeries.data?.data || []).length === 0) {
    const explanation = timeSeries.data?.meta?.explanation || [];
    return <EmptyResultExplanation explanation={explanation} />;
  }

  return (
    <Flex
      key={`resize-event-${props.resizeTriggerKey || ""}`}
      centered
      gap={0}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{ position: "relative" }}
    >
      {isHovering && (
        <Button
          type="text"
          size="small"
          style={{
            padding: 2,
            minWidth: 24,
            position: "absolute",
            left: 0,
            zIndex: 1,
            background: token.colorBgContainer,
            boxShadow: token.boxShadowTertiary,
          }}
          onClick={() => {
            if (params.state.duration) {
              const value = previousDuration(params.state.duration);
              params.dispatch({
                type: SearchActions.setDates,
                value: [value.startDate, value.endDate],
              });
            } else if (params.state.endDate && params.state.startDate) {
              const value = previousRange(
                params.state.startDate,
                params.state.endDate
              );
              params.dispatch({
                type: SearchActions.setDates,
                value: [value.startDate, value.endDate],
              });
            }
          }}
        >
          <ArrowLeftOutlined />
        </Button>
      )}
      <Flex
        centered
        style={{ flex: 1, height: props.height, position: "relative" }}
      >
        <DynamicCanvas loading={timeSeries.isLoading}>
          {({ width, height }) => (
            <Chart
              id={id}
              width={width}
              height={height}
              data={data}
              margin={{ bottom: 8, left: 12, right: 12, top: 0 }}
              className={`time-series-chart ${displayType}`}
              onMouseDown={(state) => {
                if (!state) return;
                const startX = state.activeLabel || "";
                const startDayjs = state.activePayload?.[0]?.payload[2];
                setStart([startX, startDayjs]);
                setEnd([startX, startDayjs]);
              }}
              onMouseMove={(state) => {
                setAllowTooltip(true);
                if (!state) return;
                const endX = state.activeLabel || "";
                const endDayjs = state.activePayload?.[0]?.payload[2];
                setEnd([endX, endDayjs]);
              }}
              onMouseUp={(_, event) => {
                if (startDayjs && endDayjs) {
                  setAllowTooltip(false);
                  const reverse = startDayjs.isAfter(endDayjs);
                  const start = (reverse ? endDayjs : startDayjs).startOf(
                    timeSeries.timeUnit as OpUnitType
                  );
                  const end = (reverse ? startDayjs : endDayjs).endOf(
                    timeSeries.timeUnit as OpUnitType
                  );
                  setPopup(
                    event,
                    <CustomMenu
                      context={props.context}
                      start={start}
                      end={end}
                    />
                  );
                } else {
                  setStart(undefined);
                  setEnd(undefined);
                }
              }}
              onMouseLeave={() => {
                setStart(undefined);
                setEnd(undefined);
              }}
            >
              <XAxis
                hide={props.hideLabels}
                axisLine={false}
                tick={(props) => {
                  const { x, y, payload } = props;
                  return (
                    <g transform={`translate(${x},${y})`}>
                      <text
                        {...mono.style}
                        x={0}
                        y={0}
                        dy={4}
                        textAnchor="middle"
                        fontSize={9}
                        fontWeight={500}
                        letterSpacing="-0.03em"
                        fill={token.colorTextTertiary}
                      >
                        {payload.value}
                      </text>
                    </g>
                  );
                }}
                height={12}
                dataKey="0"
                interval={Math.ceil(data.length / 8)}
                minTickGap={40}
                padding={{ left: 12, right: 12 }}
              />
              <YAxis hide />
              {allowTooltip && (
                <Tooltip
                  cursor={{ fill: singleColor(), fillOpacity: 0.1 }}
                  content={(props: TooltipProps<number, string>) => (
                    <CustomTooltip {...props} />
                  )}
                />
              )}
              {refX1 && refX2 && (
                <ReferenceArea
                  x1={refX1}
                  x2={refX2}
                  fill={singleColor()}
                  fillOpacity={0.2}
                />
              )}
              {displayType === "bars" ? (
                <Bar
                  animationBegin={0}
                  animationDuration={0}
                  dataKey="1"
                  shape={({ x, y, width, height }) => (
                    <rect
                      x={x + 1}
                      y={y}
                      width={Math.max(1, width - 2)}
                      height={Math.max(1, height)}
                      fill={
                        height === 0 ? token.colorFillSecondary : singleColor()
                      }
                    />
                  )}
                />
              ) : (
                <Line
                  dot={false}
                  animationBegin={0}
                  animationDuration={0}
                  dataKey="1"
                  stroke={singleColor()}
                  type={"monotone"}
                  strokeOpacity={1}
                  strokeWidth={2}
                />
              )}
            </Chart>
          )}
        </DynamicCanvas>
      </Flex>
      {isHovering && (
        <Button
          type="text"
          size="small"
          style={{
            padding: 2,
            minWidth: 24,
            position: "absolute",
            right: 0,
            zIndex: 1,
            background: token.colorBgContainer,
            boxShadow: token.boxShadowTertiary,
          }}
          onClick={() => {
            if (params.state.endDate && params.state.startDate) {
              const value = nextRange(
                params.state.startDate,
                params.state.endDate
              );
              params.dispatch({
                type: SearchActions.setDates,
                value: [value.startDate, value.endDate],
              });
            }
          }}
        >
          <ArrowRightOutlined />
        </Button>
      )}
    </Flex>
  );
}

export function useTimeSeriesData(
  props: PropsWithChildren<{
    data: [string, number][];
    duration?: string;
    start?: string;
    end?: string;
    timeUnit: "days" | "hours" | "minutes";
  }>
): Array<[string, number, Dayjs]> {
  const [start, end] = useDurationOrDatesToRange(props);
  return useMemo(() => {
    const diff = Math.abs(start.diff(end, props.timeUnit));
    const formats = {
      days: "MMM D",
      minutes: "H:mm",
      hours: "ha",
    };
    const reformattedMap = Object.fromEntries(
      props.data.map((xx) => [
        dayjs
          .utc(xx[0].replace(/Z\[UTC]/, "")) // TODO remove this after VZBL-973 is fixed
          .local()
          .format(formats[props.timeUnit]),
        xx[1],
      ])
    );
    return new Array(diff + 1).fill(0).map((_, i) => {
      const label = start
        .add(i, props.timeUnit)
        .format(formats[props.timeUnit]);
      return [
        label,
        reformattedMap[label] || 0,
        start.add(i, props.timeUnit).local(),
      ];
    });
  }, [props.data, start, end, props.timeUnit]);
}

const CustomTooltip = ({
  active = false,
  label,
  ...props
}: TooltipProps<number, string>) => {
  const { token } = theme.useToken();
  const payload = [...(props.payload || [])].reverse();
  if (active) {
    return (
      <div
        style={{
          padding: 8,
          borderRadius: 2,
          fontSize: 13,
          backgroundColor: `${token.colorBgSpotlight}`,
          color: `${token.colorTextLightSolid}`,
          boxShadow: token.boxShadowTertiary,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {payload.map(({ name, value }) => (
          <span
            key={`${name}`}
            className={mono.className}
            style={{
              fontWeight: 600,
              opacity: value === 0 ? 0.4 : 1,
            }}
          >
            {valueToNumber(value || "0")}
            {pluralize(" Event", value || 0)}
          </span>
        ))}
        <div style={{ fontWeight: 400 }}>{valueToLabel(label)}</div>
      </div>
    );
  }
  return null;
};

const CustomMenu = ({
  start,
  end,
  context,
}: {
  start: Dayjs;
  end: Dayjs;
  context: SearchContext;
}) => {
  const popup = usePopupContext();
  return (
    <div style={{ width: 180, padding: 4 }}>
      {/* VZBL-1135
      <Button
        block
        style={{ textAlign: "left" }}
        type="text"
        onClick={() => {
          context.subquery.open({
            ...context.params.state,
            groupBys: [],
            aggregations: [],
            duration: undefined,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
          });
          popup.close();
        }}
      >
        <LayoutOutlined />
        Preview events
    </Button>*/}
      <Button
        block
        type="text"
        style={{ textAlign: "left" }}
        onClick={() => {
          context.params.dispatch({
            type: SearchActions.setDates,
            value: [start.toISOString(), end.toISOString()],
          });
          popup.close();
        }}
      >
        <ZoomInOutlined />
        Zoom in
      </Button>
    </div>
  );
};
